import { LOTTERY_ADD_ONS, type LotteryId as LotteryEnumsId } from '@lottocom/lottery-enums'
import { isDeepEqual } from 'remeda'
import Money from '~~/models/Money'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config, NumberConfig } from '../@types/editor'

import { EDITOR_PLAY_ID_ATTRIBUTE } from '../constants/editor'

const getRandomNumber = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

export const createPlayKey = () => `play-key-${crypto.randomUUID()}`

type QuickPickable = {
  fields: { selected: number[] }[]
  perPlayAddOn?: unknown
  quickPick?: string
  stake?: unknown
}

const toQuickPickSignature = ({ fields, perPlayAddOn, stake }: QuickPickable) =>
  JSON.stringify([fields.map(({ selected }) => selected), perPlayAddOn, stake])

export const markQuickPicked = (play: QuickPickable) =>
  (play.quickPick = toQuickPickSignature(play))

// a quick picked play stays one only until the player changes it by hand
export const isQuickPicked = (play: QuickPickable) =>
  !!play.quickPick && play.quickPick === toQuickPickSignature(play)

export const hasDeletablePlays = <TPlay>(
  plays: TPlay[],
  isPlayComplete: (play: TPlay) => boolean,
): boolean => {
  if (plays.length > 1) return true

  const [play] = plays
  return !!play && isPlayComplete(play)
}

export const pickNumbers = (
  { selectableMax, selectableNumbers }: NumberConfig,
  existing: number[] = [],
) => {
  const numbers = new Set(existing.filter((value) => selectableNumbers.includes(value)))
  const available = selectableNumbers.filter((number) => !numbers.has(number))
  while (numbers.size < selectableMax && available.length) {
    const index = getRandomNumber(0, available.length - 1)
    numbers.add(available[index])
    available.splice(index, 1)
  }
  return [...numbers].sort((a, b) => selectableNumbers.indexOf(a) - selectableNumbers.indexOf(b))
}

export const toPlaySelector = (playId: string) => `[${EDITOR_PLAY_ID_ATTRIBUTE}="${playId}"]`

export const pickRandomEntry = <TEntry>(entries: TEntry[]): Maybe<TEntry> =>
  entries.length ? (entries[getRandomNumber(0, entries.length - 1)] ?? null) : null

export const validatePlay = (
  lotteryId: LotteryId,
  config: Config,
  betType: Maybe<string>,
  numbers: Maybe<Maybe<number>[]>,
): boolean => {
  if (!(lotteryId in LOTTERY_ADD_ONS)) return true

  const selectedByField = (numbers ?? []).map((entry) =>
    entry === null || entry === undefined ? null : [entry],
  )

  const onlyNecessaryNumbers = config.numbers
    .map(({ ignoredForAddOns }, index) => {
      const selected = selectedByField[index] ?? []
      if (!selected.length) return null
      const isIgnored = ignoredForAddOns.some(({ value }) => value === betType)
      return isIgnored ? null : selected
    })
    .filter((n): n is number[] => n !== null)
    .flat()

  const disallowedPlays = LOTTERY_ADD_ONS[lotteryId as unknown as LotteryEnumsId].betTypes?.find(
    ({ name }) => name === betType,
  )?.disallowedPlays
  if (!disallowedPlays) return true

  return !disallowedPlays.some((disallowed) => isDeepEqual(onlyNecessaryNumbers, disallowed))
}

export const toStakeMoney = (value: number): string =>
  new Money(value / 100).toString({ fractionDigits: 0 })
